import React, { useState, useRef } from 'react';
import api from '../api';
import { IconScan, IconCheck, IconAlert, IconX, IconRefresh } from './Icons';

const STATUS = { idle: 'idle', uploading: 'uploading', reviewing: 'reviewing', applying: 'applying', done: 'done', error: 'error' };

/* ── Delta badge ───────────────────────────────────────────────── */
function DeltaBadge({ label, count, color }) {
  if (!count) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: color + '18', color, borderRadius: 99, padding: '3px 10px', fontSize: 12, fontWeight: 700, border: `1px solid ${color}30` }}>
      {label} · {count}
    </span>
  );
}

/* ── Delta panel shown above review table ──────────────────────── */
function DeltaPanel({ delta, isFirstScan, previousScan, onAnswerMissing }) {
  if (isFirstScan) {
    return (
      <div style={{ background: 'rgba(13,148,136,.06)', border: '1px solid rgba(13,148,136,.2)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#0D9488' }}>
        <strong>First scan recorded.</strong> Next time you upload, I'll compare and flag what's changed.
      </div>
    );
  }

  if (!delta) return null;

  const { missing, dropped, increased, newItems } = delta;
  const hasDelta = missing.length || dropped.length || increased.length || newItems.length;

  if (!hasDelta) {
    return (
      <div style={{ background: 'rgba(34,197,94,.06)', border: '1px solid rgba(34,197,94,.2)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#16a34a' }}>
        <IconCheck width={13} height={13} stroke="#16a34a" strokeWidth={2.5} /> <strong>Looks consistent</strong> with your last scan on {new Date(previousScan.scanned_at).toLocaleDateString()}.
      </div>
    );
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 16, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: '#0f172a', marginRight: 4 }}>
          Changes since {new Date(previousScan.scanned_at).toLocaleDateString()}
        </span>
        <DeltaBadge label="Missing" count={missing.length} color="#ef4444" />
        <DeltaBadge label="Low" count={dropped.length} color="#f59e0b" />
        <DeltaBadge label="Up" count={increased.length} color="#0D9488" />
        <DeltaBadge label="New" count={newItems.length} color="#6366f1" />
      </div>

      {/* Missing items — needs a yes/no answer */}
      {missing.length > 0 && (
        <div style={{ padding: '12px 16px', borderBottom: newItems.length || dropped.length || increased.length ? '1px solid #f1f5f9' : 'none' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 10 }}>
            Not seen this scan — confirm status
          </div>
          {missing.map((m, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, fontSize: 13 }}>
                <strong>{m.name}</strong>
                <span style={{ color: '#64748b', marginLeft: 6 }}>was {m.lastQty} {m.lastUnit}</span>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button
                  className="btn btn-sm"
                  style={{ background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                  onClick={() => onAnswerMissing(m.name, 'out_of_stock')}
                >
                  Out of stock
                </button>
                <button
                  className="btn btn-sm"
                  style={{ background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                  onClick={() => onAnswerMissing(m.name, 'not_in_frame')}
                >
                  Not in frame
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dropped quantities */}
      {dropped.length > 0 && (
        <div style={{ padding: '12px 16px', borderBottom: (newItems.length || increased.length) ? '1px solid #f1f5f9' : 'none' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>
            Quantity dropped
          </div>
          {dropped.map((d, i) => (
            <div key={i} style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
              <strong>{d.name}</strong>
              <span style={{ color: '#64748b' }}>{d.prevQty} → {d.currQty} {d.unit}</span>
              <span style={{ color: '#ef4444', fontWeight: 700 }}>{d.pctChange}%</span>
            </div>
          ))}
        </div>
      )}

      {/* Increased quantities */}
      {increased.length > 0 && (
        <div style={{ padding: '12px 16px', borderBottom: newItems.length ? '1px solid #f1f5f9' : 'none' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#0D9488', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>
            Quantity increased
          </div>
          {increased.map((d, i) => (
            <div key={i} style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
              <strong>{d.name}</strong>
              <span style={{ color: '#64748b' }}>{d.prevQty} → {d.currQty} {d.unit}</span>
              <span style={{ color: '#0D9488', fontWeight: 700 }}>+{d.pctChange}%</span>
            </div>
          ))}
        </div>
      )}

      {/* New items */}
      {newItems.length > 0 && (
        <div style={{ padding: '12px 16px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>
            New — not seen before
          </div>
          {newItems.map((n, i) => (
            <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>
              <strong>{n.name}</strong> <span style={{ color: '#64748b' }}>{n.quantity} {n.unit}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const VARIANCE_REASONS = ['Waste', 'Spoilage', 'Theft', 'Comp / Giveaway', 'Vendor short', 'Counted wrong', 'Other'];

/* ── Inventory reconciliation panel ────────────────────────────── */
function InventoryDeltaPanel({ inventoryDelta, onReasonChange, reasons }) {
  if (!inventoryDelta) return null;
  const { matched, notInSystem, notCounted } = inventoryDelta;

  // Only show matched items with a meaningful discrepancy (>0.5 unit difference)
  const discrepant = matched.filter(m => Math.abs(m.diff) > 0.5);
  const belowPar = matched.filter(m => m.belowPar);
  const hasAnything = discrepant.length || notInSystem.length || notCounted.length || belowPar.length;

  if (!hasAnything && matched.length > 0) {
    return (
      <div style={{ background: 'rgba(34,197,94,.06)', border: '1px solid rgba(34,197,94,.2)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#16a34a' }}>
        <strong>✓ Counts match inventory</strong> — {matched.length} item{matched.length !== 1 ? 's' : ''} reconciled, no discrepancies found.
      </div>
    );
  }

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 16, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', background: 'rgba(99,102,241,0.06)', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>vs. Inventory System</span>
        {discrepant.length > 0 && <span style={{ fontSize: 12, fontWeight: 700, background: 'rgba(239,68,68,.12)', color: '#ef4444', borderRadius: 99, padding: '2px 10px' }}>
          {discrepant.length} variance{discrepant.length !== 1 ? 's' : ''}
        </span>}
        {belowPar.length > 0 && <span style={{ fontSize: 12, fontWeight: 700, background: 'rgba(245,158,11,.12)', color: '#f59e0b', borderRadius: 99, padding: '2px 10px' }}>
          {belowPar.length} below par
        </span>}
        {notInSystem.length > 0 && <span style={{ fontSize: 12, fontWeight: 700, background: 'rgba(99,102,241,.12)', color: '#6366f1', borderRadius: 99, padding: '2px 10px' }}>
          {notInSystem.length} not in system
        </span>}
      </div>

      {discrepant.length > 0 && (
        <div style={{ padding: '12px 16px', borderBottom: (notInSystem.length || notCounted.length) ? '1px solid #f1f5f9' : 'none' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 10 }}>
            Count vs System
          </div>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: '#94a3b8', fontSize: 11 }}>
                <th style={{ textAlign: 'left', paddingBottom: 6, fontWeight: 600 }}>Item</th>
                <th style={{ textAlign: 'right', paddingBottom: 6, fontWeight: 600 }}>Counted</th>
                <th style={{ textAlign: 'right', paddingBottom: 6, fontWeight: 600 }}>System</th>
                <th style={{ textAlign: 'right', paddingBottom: 6, fontWeight: 600 }}>Variance</th>
                <th style={{ paddingBottom: 6, fontWeight: 600, paddingLeft: 8 }}>Reason (optional)</th>
              </tr>
            </thead>
            <tbody>
              {discrepant.map((m, i) => (
                <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '6px 0' }}>
                    <strong>{m.name}</strong>
                    {m.belowPar && <span style={{ marginLeft: 6, fontSize: 10, background: 'rgba(245,158,11,.15)', color: '#f59e0b', borderRadius: 4, padding: '1px 6px' }}>below par {m.parLevel}</span>}
                  </td>
                  <td style={{ textAlign: 'right', padding: '6px 0', color: '#0f172a' }}>{m.scannedQty} {m.unit}</td>
                  <td style={{ textAlign: 'right', padding: '6px 0', color: '#64748b' }}>{m.systemQty} {m.unit}</td>
                  <td style={{ textAlign: 'right', padding: '6px 0', fontWeight: 700, color: m.diff < 0 ? '#ef4444' : '#0D9488' }}>
                    {m.diff > 0 ? '+' : ''}{m.diff.toFixed(1)} {m.unit}
                  </td>
                  <td style={{ padding: '6px 0 6px 8px' }}>
                    <select
                      value={reasons?.[m.name] || ''}
                      onChange={e => onReasonChange?.(m.name, e.target.value)}
                      style={{
                        fontSize: 11, padding: '3px 6px', borderRadius: 5, border: '1px solid rgba(255,255,255,0.15)',
                        background: reasons?.[m.name] ? 'rgba(13,148,136,0.12)' : 'rgba(255,255,255,0.05)',
                        color: 'inherit', cursor: 'pointer', maxWidth: 120,
                      }}
                    >
                      <option value="">— reason</option>
                      {VARIANCE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {notInSystem.length > 0 && (
        <div style={{ padding: '12px 16px', borderBottom: notCounted.length ? '1px solid #f1f5f9' : 'none' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>
            Scanned — not in system yet
          </div>
          {notInSystem.map((n, i) => (
            <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>
              <strong>{n.name}</strong> <span style={{ color: '#64748b' }}>{n.quantity} {n.unit}</span>
              <span style={{ marginLeft: 8, fontSize: 11, color: '#6366f1' }}>will be created</span>
            </div>
          ))}
        </div>
      )}

      {notCounted.length > 0 && (
        <div style={{ padding: '12px 16px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>
            In system — not counted this scan
          </div>
          {notCounted.map((n, i) => (
            <div key={i} style={{ fontSize: 13, marginBottom: 4, color: '#64748b' }}>
              {n.name} <span style={{ marginLeft: 4 }}>({n.systemQty} {n.unit} on record{n.belowPar ? ', below par' : ''})</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Main component ────────────────────────────────────────────── */
export default function InventoryScanner({ onApplied }) {
  const [status, setStatus] = useState(STATUS.idle);
  const [preview, setPreview] = useState(null);
  const [items, setItems] = useState([]);
  const [delta, setDelta] = useState(null);
  const [inventoryDelta, setInventoryDelta] = useState(null);
  const [varianceReasons, setVarianceReasons] = useState({});
  const [isFirstScan, setIsFirstScan] = useState(false);
  const [previousScan, setPreviousScan] = useState(null);
  const [missingAnswers, setMissingAnswers] = useState({});
  const [error, setError] = useState('');
  const [applyResult, setApplyResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef();

  async function handleFile(file) {
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setStatus(STATUS.uploading);
    setError('');

    const formData = new FormData();
    formData.append('image', file);

    try {
      const { data } = await api.post('/inventory/scan-sheet', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (data.parseError) {
        setError("Claude read the image but couldn't parse structured data. Try a clearer photo.");
        setStatus(STATUS.error);
        return;
      }
      if (!data.items?.length) {
        setError('No inventory items found. Make sure the photo shows items clearly.');
        setStatus(STATUS.error);
        return;
      }

      setItems(data.items);
      setDelta(data.delta || null);
      setInventoryDelta(data.inventoryDelta || null);
      setIsFirstScan(data.isFirstScan || false);
      setPreviousScan(data.previousScan || null);
      setMissingAnswers({});
      setStatus(STATUS.reviewing);
    } catch (err) {
      setError(err.response?.data?.error || 'Scan failed — please try again.');
      setStatus(STATUS.error);
    }
  }

  function handleAnswerMissing(name, answer) {
    setMissingAnswers(prev => ({ ...prev, [name]: answer }));
    // If out of stock, add it to the items list with qty 0
    if (answer === 'out_of_stock') {
      setItems(prev => {
        const exists = prev.find(i => i.name.toLowerCase() === name.toLowerCase());
        if (exists) return prev.map(i => i.name.toLowerCase() === name.toLowerCase() ? { ...i, quantity: 0, selected: true } : i);
        return [...prev, { _id: Date.now(), name, quantity: 0, unit: '', location: '', selected: true, uncertain: false }];
      });
    }
  }

  function updateItem(idx, field, value) {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  }
  function removeItem(idx) { setItems(prev => prev.filter((_, i) => i !== idx)); }
  function toggleSelect(idx) { setItems(prev => prev.map((it, i) => i === idx ? { ...it, selected: !it.selected } : it)); }
  function selectAll(v) { setItems(prev => prev.map(it => ({ ...it, selected: v }))); }

  async function handleApply() {
    const selected = items.filter(i => i.selected);
    if (!selected.length) { setError('Select at least one item.'); return; }
    setStatus(STATUS.applying);
    setError('');
    try {
      const { data } = await api.post('/inventory/apply-scan', { items: selected, variance_reasons: varianceReasons });
      setApplyResult(data);
      setStatus(STATUS.done);
      onApplied && onApplied(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply inventory.');
      setStatus(STATUS.reviewing);
    }
  }

  function reset() {
    setStatus(STATUS.idle); setPreview(null); setItems([]);
    setDelta(null); setIsFirstScan(false); setPreviousScan(null);
    setMissingAnswers({}); setError(''); setApplyResult(null); setInventoryDelta(null); setVarianceReasons({});
  }

  /* Done */
  if (status === STATUS.done && applyResult) {
    const updated = applyResult.results.filter(r => r.action === 'updated').length;
    const created = applyResult.results.filter(r => r.action === 'created').length;
    return (
      <div className="card">
        <div className="card-header" style={{ background: '#dcfce7', color: '#166534', display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconCheck width={16} height={16} stroke="#166534" /> Inventory Updated
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
            {updated > 0 && <div className="stat-card success" style={{ flex: 1 }}><div className="stat-label">Updated</div><div className="stat-value">{updated}</div><div className="stat-sub">existing items</div></div>}
            {created > 0 && <div className="stat-card" style={{ flex: 1 }}><div className="stat-label">Created</div><div className="stat-value">{created}</div><div className="stat-sub">new items</div></div>}
          </div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Item</th><th>Qty</th><th>Unit</th><th>Action</th></tr></thead>
                <tbody>
                  {applyResult.results.map((r, i) => (
                    <tr key={i}>
                      <td><strong>{r.item.name}</strong></td>
                      <td>{r.item.quantity_on_hand}</td>
                      <td>{r.item.unit || '—'}</td>
                      <td><span className={`badge ${r.action === 'created' ? 'badge-green' : 'badge-amber'}`}>{r.action}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={reset}>
            <IconScan width={14} height={14} /> Scan Another
          </button>
        </div>
      </div>
    );
  }

  /* Review */
  if (status === STATUS.reviewing || status === STATUS.applying) {
    const selectedCount = items.filter(i => i.selected).length;
    const unansweredMissing = delta?.missing?.filter(m => !missingAnswers[m.name]) || [];

    return (
      <div>
        {/* Scan summary bar */}
        <div className="card mb-4">
          <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {preview && <img src={preview} alt="Scan" style={{ height: 72, width: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0', flexShrink: 0 }} />}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Review Scan Results</div>
              <div className="text-sm text-gray">Claude found <strong>{items.length}</strong> items. Answer any questions below, then review and apply.</div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={reset}><IconX width={14} height={14} /> Start over</button>
          </div>
        </div>

        {/* Inventory reconciliation — scanned counts vs system */}
        <InventoryDeltaPanel
          inventoryDelta={inventoryDelta}
          reasons={varianceReasons}
          onReasonChange={(name, reason) => setVarianceReasons(prev => ({ ...prev, [name]: reason }))}
        />

        {/* Scan-to-scan delta — vs previous scan */}
        <DeltaPanel
          delta={delta}
          isFirstScan={isFirstScan}
          previousScan={previousScan}
          onAnswerMissing={handleAnswerMissing}
        />

        {error && <div className="alert alert-error mb-4">{error}</div>}

        {/* Item table */}
        <div className="card mb-4">
          <div style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={items.every(i => i.selected)} onChange={e => selectAll(e.target.checked)} />
              Select all
            </label>
            <span className="text-sm text-gray" style={{ marginLeft: 'auto' }}>{selectedCount} of {items.length} selected</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th style={{ width: 36 }}></th><th>Item Name</th><th style={{ width: 110 }}>Quantity</th><th style={{ width: 100 }}>Unit</th><th>Location</th><th style={{ width: 36 }}></th></tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx} style={{ opacity: item.selected ? 1 : 0.4, background: item.quantity === 0 ? '#fef2f2' : undefined }}>
                    <td><input type="checkbox" checked={!!item.selected} onChange={() => toggleSelect(idx)} /></td>
                    <td>
                      <input type="text" value={item.name} onChange={e => updateItem(idx, 'name', e.target.value)} style={{ width: '100%', padding: '4px 8px', fontSize: 13 }} />
                      {item.uncertain && <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}><IconAlert width={11} height={11} stroke="#f59e0b" /><span style={{ fontSize: 11, color: '#f59e0b' }}>unclear — verify</span></div>}
                      {item.quantity === 0 && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>Marked out of stock</div>}
                    </td>
                    <td><input type="number" min="0" step="0.5" value={item.quantity} onChange={e => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)} style={{ width: '100%', padding: '4px 8px', fontSize: 13 }} /></td>
                    <td><input type="text" value={item.unit} onChange={e => updateItem(idx, 'unit', e.target.value)} placeholder="lb, ea…" style={{ width: '100%', padding: '4px 8px', fontSize: 13 }} /></td>
                    <td><input type="text" value={item.location} onChange={e => updateItem(idx, 'location', e.target.value)} placeholder="Walk-in A…" style={{ width: '100%', padding: '4px 8px', fontSize: 13 }} /></td>
                    <td><button onClick={() => removeItem(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }} title="Remove"><IconX width={14} height={14} stroke="#ef4444" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {unansweredMissing.length > 0 && (
          <div className="alert alert-warning mb-4">
            <IconAlert width={14} height={14} stroke="#f59e0b" /> Please answer the {unansweredMissing.length} missing item question{unansweredMissing.length > 1 ? 's' : ''} above before applying.
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button className="btn btn-primary" onClick={handleApply} disabled={status === STATUS.applying || selectedCount === 0}>
            {status === STATUS.applying ? 'Applying…' : <><IconCheck width={14} height={14} /> Apply {selectedCount} Item{selectedCount !== 1 ? 's' : ''}</>}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={reset}>Cancel</button>
          <span className="text-xs text-gray" style={{ marginLeft: 'auto' }}>Existing items updated · New items created</span>
        </div>
      </div>
    );
  }

  /* Upload */
  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <IconScan width={16} height={16} stroke="#0D9488" /> Scan Inventory
      </div>
      <div className="card-body">
        {error && <div className="alert alert-error mb-4">{error}</div>}
        {status === STATUS.idle || status === STATUS.error ? (
          <>
            <p className="text-sm text-gray mb-4">
              Photograph your walk-in, shelf, or count sheet. Claude identifies what it sees, compares to your last scan, and flags what's changed.
            </p>
            <div
              className={`upload-zone${dragOver ? ' drag-over' : ''}`}
              onClick={() => inputRef.current.click()}
              onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
            >
              <div className="upload-icon"><IconScan width={40} height={40} stroke="#0D9488" strokeWidth={1.5} /></div>
              <p className="upload-text"><strong>Tap to take a photo</strong><br />Walk-in · shelf · count sheet</p>
              <p className="text-xs text-gray mt-2">JPG, PNG — up to 20MB</p>
            </div>
            <input ref={inputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            {preview && <img src={preview} alt="Preview" style={{ maxHeight: 180, borderRadius: 8, marginBottom: 20, border: '1px solid #e2e8f0' }} />}
            <div className="text-gray mb-3">{status === STATUS.uploading ? 'Uploading…' : 'Claude is reading your inventory…'}</div>
            <div className="progress-bar" style={{ maxWidth: 280, margin: '0 auto' }}>
              <div className="progress-fill good" style={{ width: status === STATUS.uploading ? '25%' : '75%', transition: 'width 2s ease' }} />
            </div>
            <div className="text-xs text-gray mt-3">Usually takes 5–15 seconds</div>
          </div>
        )}
      </div>
    </div>
  );
}
